/**
 * EventCard — placeholder card for an event in the short guide
 *
 * Matches the source doc format: threat/event summary, 2 lead-in questions,
 * countermeasures, primary competency. Flat style with burnt orange
 * left border. Below: 2 expandable ▶ links.
 */

import type { EventAnalysis } from '../../types/debriefing-guide';
import ExpandableSection from './ExpandableSection';
import CompetencyBadge from './CompetencyBadge';

interface EventCardProps {
  event: EventAnalysis;
  index: number;
  expandedSections: Record<string, boolean>;
  onToggleSection: (sectionId: string) => void;
  renderQuestionBank?: () => React.ReactNode;
  renderClassification?: () => React.ReactNode;
}

export default function EventCard({
  event,
  index,
  expandedSections,
  onToggleSection,
  renderQuestionBank,
  renderClassification,
}: EventCardProps) {
  const prefix = `event_${index}`;

  return (
    <div className="mb-3">
      {/* Placeholder card — burnt orange left border */}
      <div className="border-l-[3px] border-l-[#BF4E14] bg-white rounded-r-lg border border-l-0 border-steel/15 p-4">
        {/* Event summary + primary competency */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className="text-sm font-medium text-gray-900">{event.factsSummary}</p>
          <CompetencyBadge code={event.primaryCompetency.code} name={event.primaryCompetency.name} />
        </div>

        {/* 2 lead-in questions */}
        <div className="mb-2">
          {event.facilitatorQuestions.slice(0, 2).map((q, qIdx) => (
            <p key={qIdx} className="text-sm text-gray-600 pl-3 border-l-2 border-gray-200 mb-1">
              {q}
            </p>
          ))}
        </div>

        {/* Countermeasures */}
        {event.countermeasures.length > 0 && (
          <p className="text-xs text-gray-500">
            <span className="font-medium">Countermeasures:</span> {event.countermeasures.join('; ')}
          </p>
        )}
      </div>

      {/* 2 expandable links below */}
      <div className="ml-1 mt-0.5">
        <ExpandableSection
          label="Click to view facilitator question bank & OB classification for the event"
          expanded={!!expandedSections[`${prefix}_question_bank`]}
          onToggle={() => onToggleSection(`${prefix}_question_bank`)}
        >
          {renderQuestionBank ? renderQuestionBank() : <QuestionBankContent event={event} />}
        </ExpandableSection>

        <ExpandableSection
          label="Click to expand additional event classification blocks for the event"
          expanded={!!expandedSections[`${prefix}_classification`]}
          onToggle={() => onToggleSection(`${prefix}_classification`)}
        >
          {renderClassification ? renderClassification() : <ClassificationContent event={event} />}
        </ExpandableSection>
      </div>
    </div>
  );
}

function QuestionBankContent({ event }: { event: EventAnalysis }) {
  return (
    <div className="space-y-3">
      <div>
        <h5 className="text-xs font-semibold text-steel mb-1">Full Facilitator Questions</h5>
        <ol className="list-decimal list-inside text-sm space-y-1">
          {event.facilitatorQuestions.map((q, i) => (
            <li key={i} className="text-blue-700">{q}</li>
          ))}
        </ol>
      </div>
      <div>
        <h5 className="text-xs font-semibold text-steel mb-1">Observable Behaviors</h5>
        <p className="text-sm text-gray-700">{event.primaryCompetency.behavioralDescription}</p>
        {event.primaryCompetency.observableBehaviors.length > 0 && (
          <ul className="list-disc list-inside text-sm mt-1 space-y-1">
            {event.primaryCompetency.observableBehaviors.map((ob, i) => (
              <li key={i} className="text-gray-600">
                <span className="font-mono text-xs text-steel">{ob.code}</span>{' '}
                {ob.description}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ClassificationContent({ event }: { event: EventAnalysis }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <span className="text-xs text-steel">TEM Classification:</span>
          <p className="font-medium">{event.eventType}</p>
        </div>
        <div>
          <span className="text-xs text-steel">Primary Competency:</span>
          <p className="font-medium">
            {event.primaryCompetency.code} — {event.primaryCompetency.name}
          </p>
        </div>
      </div>
      {event.secondaryCompetencies.length > 0 && (
        <div>
          <span className="text-xs text-steel">Secondary Competencies:</span>
          <div className="flex gap-1 mt-1 flex-wrap">
            {event.secondaryCompetencies.map((c) => (
              <CompetencyBadge key={c.code} code={c.code} name={c.name} />
            ))}
          </div>
        </div>
      )}
      {event.countermeasures.length > 0 && (
        <div>
          <span className="text-xs text-steel">Countermeasures:</span>
          <ul className="list-disc list-inside text-sm mt-1">
            {event.countermeasures.map((cm, i) => (
              <li key={i} className="text-green-700">{cm}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
